'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearCampaignWorkspace,
    getCampaignWorkspace,
    listCampaignThreadMessages,
    payoutEscrow,
    approveDeliveryExtension,
    rejectDeliveryExtension,
} from '../../../redux/buzzbridge/actions';
import type {
    ICampaignWorkspaceData,
    WorkspacePerspective,
    WorkspaceMessageType,
} from '../../../redux/buzzbridge/campaignWorkspace.types';
import {
    campaignWorkspaceErrorSelector,
    campaignWorkspaceSelector,
    gettingCampaignWorkspaceSelector,
    loadingOlderCampaignThreadMessagesSelector,
    payingOutSelector,
    savingInfluencerReviewSelector,
    influencerReviewSaveErrorSelector,
    reviewingDeliveryExtensionSelector,
} from '../../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { buzzBridgeConfigSelector } from '../../../selectors/selectors';
import { ParticipantProfileHeader } from './ParticipantProfileHeader';
import { ParticipantSubmittedWork } from './ParticipantSubmittedWork';
import { ParticipantDeliverables } from './ParticipantDeliverables';
import { ParticipantWorkspaceDiscussion } from './ParticipantWorkspaceDiscussion';
import { buildDeliverableRows, mapWorkspaceToProfileHeader } from './campaignWorkspaceViewMappers';
import { CircuulCreatorStatsCard } from './CircuulCreatorStatsCard';
import { DialogueModal } from '../../../components/DialogueModal/DialogueModal';
import { DELIVERABLE_TYPE_LABELS } from '../../../utils/deliverableLabels';
import InfluencerReviewModal from '../../../components/BuzzBridge/InfluencerReviewModal';
import { useCampaignThreadMarkReadOnOpen } from '../../../hook/useCampaignThreadMarkReadOnOpen';
import { useCampaignThreadSocketRoom } from '../../../hook/useCampaignThreadSocketRoom';

export interface CampaignWorkspaceShellProps {
    perspective: WorkspacePerspective;
    participantId: string;
    /** Current user id (discussion alignment). */
    currentUserId?: string;
    /** POST /messages body `messageType` (must be allowed for caller). */
    threadMessageType?: WorkspaceMessageType;
    renderBreadcrumbs: (ctx: { workspace: ICampaignWorkspaceData | null; loading: boolean }) => React.ReactNode;
}

export function CampaignWorkspaceShell({
    perspective,
    participantId,
    currentUserId,
    threadMessageType = 'all_participants',
    renderBreadcrumbs,
}: CampaignWorkspaceShellProps) {
    const dispatch = useDispatch();
    const workspace = useSelector(campaignWorkspaceSelector);
    const loading = useSelector(gettingCampaignWorkspaceSelector);
    const loadingOlder = useSelector(loadingOlderCampaignThreadMessagesSelector);
    const error = useSelector(campaignWorkspaceErrorSelector);
    const payingOut = useSelector(payingOutSelector);
    const savingInfluencerReview = useSelector(savingInfluencerReviewSelector);
    const influencerReviewSaveError = useSelector(influencerReviewSaveErrorSelector);
    const reviewingExtension = useSelector(reviewingDeliveryExtensionSelector);

    const pid = participantId?.trim() || '';
    const [activeRoundIdx, setActiveRoundIdx] = useState(0);

    useCampaignThreadMarkReadOnOpen({
        participantId: pid,
        campaignId: workspace?.campaign._id,
        perspective,
        enabled: Boolean(workspace && !loading),
    });

    useCampaignThreadSocketRoom(pid, Boolean(pid && workspace && !loading));

    useEffect(() => {
        if (!pid) return;
        dispatch(clearCampaignWorkspace());
        dispatch(
            getCampaignWorkspace({
                perspective,
                participantId: pid,
                messagesPage: 1,
                messagesLimit: 20,
                appendMessages: false,
            }),
        );
        return () => {
            dispatch(clearCampaignWorkspace());
        };
    }, [dispatch, perspective, pid, currentUserId]);

    const handleRetry = useCallback(() => {
        if (!pid) return;
        dispatch(
            getCampaignWorkspace({
                perspective,
                participantId: pid,
                messagesPage: 1,
                messagesLimit: 20,
                appendMessages: false,
            }),
        );
    }, [dispatch, perspective, pid]);

    const loadOlder = useCallback(() => {
        if (!pid || !workspace?.messages_pagination) return;
        const { page, has_more_older } = workspace.messages_pagination;
        if (!has_more_older) return;
        dispatch(
            listCampaignThreadMessages({
                participantId: pid,
                messagesPage: page + 1,
                messagesLimit: workspace.messages_pagination.limit || 20,
                append: true,
            }),
        );
    }, [dispatch, pid, workspace]);

    const creatorName =
        workspace?.creator.influencer?.name?.trim() ||
        (workspace?.creator.user && 'userName' in workspace.creator.user ? workspace.creator.user.userName : null) ||
        (workspace?.creator.user && 'userName' in workspace.creator.user ? workspace.creator.user.userName : null) ||
        'Creator';

    const creatorCounterpartIds = useMemo(() => {
        const ids: string[] = [];
        const user = workspace?.creator?.user;
        if (user && typeof user === 'object' && '_id' in user && user._id) {
            ids.push(String(user._id));
        }
        const influencer = workspace?.creator?.influencer;
        if (influencer?._id) {
            ids.push(String(influencer._id));
        }
        return ids;
    }, [workspace]);

    // canPayout: for direct hire and commission campaigns, just need approved subs (no live URL required);
    // otherwise every campaign deliverable must have an approved submission with a live URL
    const isDirectHire = workspace?.campaign?.campaign_type === 'direct_hire';
    const isCommission = workspace?.campaign?.campaign_type === 'commission';
    const isAppInstall = !!workspace?.campaign?.app_install?.enabled;
    const noLiveUrlRequired = isDirectHire || isCommission || isAppInstall;
    const allSubs = workspace?.participant.content_submissions ?? [];
    const approvedSubs = allSubs.filter(s => s.status === 'approved' || s.status === 'shared');
    const requiredDeliverables = workspace?.campaign?.deliverables ?? [];

    const blockingDeliverables = noLiveUrlRequired
        ? []
        : requiredDeliverables
              .filter(d => {
                  const matchingSub = approvedSubs.find(s => s.deliverable_type === d.type);
                  return !matchingSub || !(matchingSub.live_urls?.length ?? 0);
              })
              .map(d => DELIVERABLE_TYPE_LABELS[d.type] ?? d.type);

    // Total required approved submissions, counting each slot for multi-upload deliverables
    const totalRequiredApprovedSlots =
        requiredDeliverables.reduce((sum, d) => {
            const mu = (d as any).multiple_uploads;
            return sum + (mu?.enabled && mu.count >= 2 ? mu.count : 1);
        }, 0) || 1;

    // For multi-upload deliverables, verify all slots are approved
    const allSlotsApproved = requiredDeliverables.every(d => {
        const mu = (d as any).multiple_uploads;
        if (!mu?.enabled || mu.count < 2) {
            return approvedSubs.some(s => s.deliverable_type === d.type);
        }
        for (let i = 0; i < mu.count; i++) {
            if (!approvedSubs.some(s => s.deliverable_type === d.type && (s.slot_index ?? 0) === i)) {
                return false;
            }
        }
        return true;
    });

    const canPayout =
        workspace?.participant?.status !== 'completed' &&
        approvedSubs.length > 0 &&
        (noLiveUrlRequired
            ? approvedSubs.length >= totalRequiredApprovedSlots && allSlotsApproved
            : blockingDeliverables.length === 0 && requiredDeliverables.length > 0);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [influencerReviewOpen, setInfluencerReviewOpen] = useState(false);

    const wasSavingInfluencerReviewRef = useRef(false);
    useEffect(() => {
        if (wasSavingInfluencerReviewRef.current && !savingInfluencerReview && !influencerReviewSaveError) {
            setInfluencerReviewOpen(false);
        }
        wasSavingInfluencerReviewRef.current = savingInfluencerReview;
    }, [savingInfluencerReview, influencerReviewSaveError]);

    const handleMarkComplete = useCallback(() => {
        if (!canPayout) return;
        setConfirmOpen(true);
    }, [canPayout]);

    const handleConfirmPayout = useCallback(() => {
        if (!workspace || !canPayout) return;
        setConfirmOpen(false);
        const campaignId = workspace.campaign._id;
        // Commission / app-install campaigns: escrow payout is base pay only; commission earnings come via commission wallet
        const amount_cents =
            (isCommission || isAppInstall) && workspace.campaign.base_payment_per_creator_cents
                ? Math.round(Number(workspace.campaign.base_payment_per_creator_cents))
                : Number(workspace.possible_earning_cents ?? 0);
        dispatch(payoutEscrow({ campaignId, amount_cents, participant_id: pid }));
    }, [dispatch, workspace, canPayout, pid, isCommission, isAppInstall]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
            {renderBreadcrumbs({ workspace, loading })}

            {error && !workspace ? (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={handleRetry}>
                            Retry
                        </Button>
                    }>
                    Could not load campaign workspace.
                </Alert>
            ) : null}

            {loading && !workspace ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {workspace ? (
                <>
                    <Box id="participant-profile-header">
                        <ParticipantProfileHeader
                            {...mapWorkspaceToProfileHeader(workspace)}
                            onMarkComplete={handleMarkComplete}
                            markCompleteLoading={payingOut}
                            canMarkComplete={canPayout}
                            blockingDeliverables={blockingDeliverables}
                        />
                    </Box>
                    {perspective === 'brand' && workspace.circuul_stats ? (
                        <CircuulCreatorStatsCard stats={workspace.circuul_stats} />
                    ) : null}
                    {perspective === 'brand' ? (
                        <Box id="influencer-review-prompt">
                            <InfluencerReviewPrompt
                                workspace={workspace}
                                onOpenReview={() => setInfluencerReviewOpen(true)}
                            />
                        </Box>
                    ) : null}
                    {perspective === 'brand' && workspace.participant.status !== 'completed' && (
                        <AutoBenchmarkBanner workspace={workspace} />
                    )}
                    {perspective === 'brand' &&
                        (workspace.participant as any).delivery_extension_requests?.some(
                            (r: any) => r.status === 'pending',
                        ) && (
                            <Box
                                sx={{
                                    bgcolor: '#1a1919',
                                    border: '1px solid rgba(255,201,101,0.2)',
                                    borderRadius: 2,
                                    p: 2.5,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: '#ffc965',
                                        mb: 1.5,
                                    }}>
                                    Delivery Date Extension Request
                                </Typography>
                                {((workspace.participant as any).delivery_extension_requests as any[])
                                    .filter((r: any) => r.status === 'pending')
                                    .map((r: any) => (
                                        <Box
                                            key={r._id}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.75,
                                                mb: 2,
                                            }}>
                                            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                                                {creatorName} is requesting{' '}
                                                <strong>
                                                    {r.requested_days} extra day{r.requested_days === 1 ? '' : 's'}
                                                </strong>{' '}
                                                — new date:{' '}
                                                <strong>
                                                    {new Date(r.new_delivery_date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    })}
                                                </strong>
                                            </Typography>
                                            {r.reason ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: 'rgba(255,255,255,0.5)',
                                                        fontStyle: 'italic',
                                                    }}>
                                                    "{r.reason}"
                                                </Typography>
                                            ) : null}
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={reviewingExtension}
                                                    onClick={() =>
                                                        dispatch(
                                                            approveDeliveryExtension({
                                                                participantId: pid,
                                                                requestId: r._id,
                                                            }),
                                                        )
                                                    }
                                                    sx={{
                                                        bgcolor: '#00fdc1',
                                                        color: '#000',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        textTransform: 'none',
                                                        '&:hover': { bgcolor: '#00d9a7' },
                                                        '&:disabled': {
                                                            bgcolor: 'rgba(0,253,193,0.3)',
                                                            color: 'rgba(0,0,0,0.4)',
                                                        },
                                                    }}>
                                                    {reviewingExtension ? (
                                                        <CircularProgress size={12} sx={{ color: '#000', mr: 0.5 }} />
                                                    ) : null}
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={reviewingExtension}
                                                    onClick={() =>
                                                        dispatch(
                                                            rejectDeliveryExtension({
                                                                participantId: pid,
                                                                requestId: r._id,
                                                            }),
                                                        )
                                                    }
                                                    sx={{
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                        color: 'rgba(255,255,255,0.7)',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255,255,255,0.35)',
                                                            bgcolor: 'rgba(255,255,255,0.04)',
                                                        },
                                                    }}>
                                                    Decline
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                            </Box>
                        )}
                    <Box id="participant-submitted-work">
                        <ParticipantSubmittedWork
                            submissions={workspace.participant.content_submissions ?? []}
                            participantId={pid}
                            isDirectHire={isDirectHire}
                            brandId={workspace.brand?._id}
                            deliverableRounds={(workspace.campaign as any).deliverable_rounds}
                            roundPayouts={workspace.participant.round_payouts}
                            campaignId={workspace.campaign._id}
                            requirements={workspace.campaign.requirements}
                            activeRoundIdx={activeRoundIdx}
                            onActiveRoundChange={setActiveRoundIdx}
                            roundDeliveryOverrides={(workspace.participant as any).round_delivery_overrides}
                            liveLinkWindows={(workspace.participant as any).live_link_windows}
                            campaignStatus={workspace.campaign.status}
                            campaignDeliveryDate={(workspace.campaign as any).delivery_date}
                            campaignEndDate={workspace.campaign.end_date}
                            submissionDeliveryDate={(workspace.participant as any).submission_delivery_date}
                            campaignDeliverables={(workspace.campaign as any).deliverables}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' },
                            gap: 3,
                            alignItems: 'start',
                        }}>
                        <Box id="participant-deliverables">
                            <ParticipantDeliverables
                                items={buildDeliverableRows(workspace)}
                                deliverableRounds={(workspace.campaign as any).deliverable_rounds}
                                roundPayouts={workspace.participant.round_payouts}
                                submissions={workspace.participant.content_submissions ?? []}
                                activeRoundIdx={activeRoundIdx}
                            />
                        </Box>
                        <Box id="participant-discussion">
                            <ParticipantWorkspaceDiscussion
                                participantId={pid}
                                messageType={threadMessageType}
                                messages={workspace.messages ?? []}
                                creatorDisplayName={creatorName}
                                counterpartDisplayName={creatorName}
                                counterpartUserIds={creatorCounterpartIds}
                                currentUserId={currentUserId}
                                hasMoreOlder={Boolean(workspace.messages_pagination?.has_more_older)}
                                loadingOlder={loadingOlder}
                                onLoadOlder={loadOlder}
                                contentSubmissions={workspace.participant.content_submissions}
                                participationBlocked={workspace.campaign?.status === 'closed'}
                            />
                        </Box>
                    </Box>
                    {perspective === 'brand' ? (
                        <InfluencerReviewModal
                            open={influencerReviewOpen}
                            onClose={() => setInfluencerReviewOpen(false)}
                            influencerId={
                                typeof workspace.creator.influencer === 'object' && workspace.creator.influencer
                                    ? workspace.creator.influencer._id
                                    : ''
                            }
                            influencerName={workspace.creator.influencer?.name}
                            influencerProfilePicture={workspace.creator.influencer?.profile_picture}
                            mode={workspace.influencer_review?.can_update ? 'edit' : 'create'}
                            initialRating={workspace.influencer_review?.review?.rating ?? 0}
                            initialReviewText={workspace.influencer_review?.review?.review_text ?? ''}
                            reviewId={workspace.influencer_review?.review?._id}
                            campaign_participant={
                                workspace.influencer_review?.suggested_campaign_participant ?? undefined
                            }
                            participantId={pid}
                        />
                    ) : null}
                </>
            ) : null}

            {!loading && !workspace && !error && !pid ? (
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.5)' }}>
                    Missing participant.
                </Typography>
            ) : null}

            <DialogueModal
                open={confirmOpen}
                onClose={() => !payingOut && setConfirmOpen(false)}
                header="Release Payment to Creator?"
                buttonEnabled
                buttonText={payingOut ? '' : 'Yes, Release Payment'}
                buttonProps={{
                    disabled: payingOut,
                    startIcon: payingOut ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined,
                }}
                onSubmit={handleConfirmPayout}
                paperSX={{ maxWidth: { xs: '90%', sm: '440px' }, backgroundColor: '#1a1919' }}
                dialogueSX={{ height: 'auto', alignItems: 'center' }}>
                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                        You're about to mark this campaign as{' '}
                        <Box component="span" sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            complete
                        </Box>{' '}
                        and release the agreed payment to{' '}
                        <Box component="span" sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {creatorName}
                        </Box>
                        .
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', mt: 2, lineHeight: 1.6 }}>
                        Please confirm that all deliverables have been fulfilled and the live content meets your
                        expectations. Once released, this payment{' '}
                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.75)', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            cannot be reversed
                        </Box>
                        .
                    </Typography>
                </Box>
            </DialogueModal>
        </Box>
    );
}

function InfluencerReviewPrompt({
    workspace,
    onOpenReview,
}: {
    workspace: ICampaignWorkspaceData;
    onOpenReview: () => void;
}) {
    const ctx = workspace.influencer_review;

    // If ctx is absent (backend hasn't populated it yet), assume eligible for first-time review.
    // Only hide if the server explicitly says not eligible AND no existing review to update.
    if (ctx && !ctx.eligible && !ctx.can_update) return null;

    const canUpdate = ctx?.can_update ?? false;
    const existingRating = ctx?.review?.rating;
    const avg = ctx?.influencer_rating?.average_rating;
    const count = ctx?.influencer_rating?.review_count;

    const GOLD = '#FFC965';
    const ON_VARIANT = 'rgba(255,255,255,0.7)';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                p: 3,
                borderRadius: 3,
                bgcolor: 'rgba(255,201,101,0.06)',
                border: '1px solid rgba(255,201,101,0.15)',
            }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: GOLD,
                        mb: 0.5,
                    }}>
                    Creator review
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: GOLD }}>
                    {canUpdate ? 'You reviewed this creator' : 'How was your collaboration?'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: ON_VARIANT, mt: 0.5, lineHeight: 1.5 }}>
                    {canUpdate
                        ? 'Update your star rating or written feedback anytime.'
                        : 'Rate the creator based on your completed campaign experience.'}
                </Typography>
                {canUpdate && existingRating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Box
                                key={i}
                                component="span"
                                sx={{ color: i < existingRating ? GOLD : 'rgba(255,255,255,0.2)', fontSize: 18 }}>
                                ★
                            </Box>
                        ))}
                        {avg != null && count != null ? (
                            <Typography sx={{ ml: 1, fontSize: 12, color: ON_VARIANT }}>
                                {avg.toFixed(1)} avg · {count} {count === 1 ? 'review' : 'reviews'}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
            </Box>
            <Button
                variant="contained"
                onClick={onOpenReview}
                sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    borderRadius: 999,
                    px: 2.5,
                    bgcolor: GOLD,
                    color: '#131517',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: GOLD, boxShadow: 'none' },
                }}>
                {canUpdate ? 'Edit review' : 'Rate creator'}
            </Button>
        </Box>
    );
}

function AutoBenchmarkBanner({ workspace }: { workspace: ICampaignWorkspaceData }) {
    const BLUE = 'rgba(100,160,255,0.9)';
    const BLUE_BG = 'rgba(100,160,255,0.06)';
    const BLUE_BORDER = 'rgba(100,160,255,0.18)';
    const GREEN = 'rgba(100,220,130,0.9)';
    const GREEN_BG = 'rgba(100,220,130,0.06)';
    const GREEN_BORDER = 'rgba(100,220,130,0.18)';
    const AMBER = 'rgba(255,193,7,0.9)';
    const AMBER_BG = 'rgba(255,193,7,0.06)';
    const AMBER_BORDER = 'rgba(255,193,7,0.18)';

    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const autoApproveDays = Math.max(1, Number(buzzBridgeConfig?.campaign_deadlines?.auto_approve_days) || 7);
    const markCompleteDays = Math.max(
        1,
        Number(buzzBridgeConfig?.campaign_deadlines?.mark_complete_deadline_days) || 7,
    );

    const isDirectHire = workspace.campaign?.campaign_type === 'direct_hire';
    const isCommissionTimeline = workspace.campaign?.campaign_type === 'commission';
    const isAppInstallTimeline = !!workspace.campaign?.app_install?.enabled;
    const noLiveUrlTimeline = isDirectHire || isCommissionTimeline || isAppInstallTimeline;
    // Prefer participant-level extension date over campaign end_date so the
    // auto-approve deadline reflects the creator's actual deadline.
    const endDate = (workspace.participant as any).submission_delivery_date ?? workspace.campaign?.end_date;
    const allSubs = workspace.participant.content_submissions ?? [];
    const approvedSubs = allSubs.filter(s => s.status === 'approved' || s.status === 'shared');

    // Find the latest approval date across all approved submissions
    const latestApprovedAt = approvedSubs
        .map(s => s.approved_at)
        .filter(Boolean)
        .sort()
        .at(-1);

    // For direct hire and commission, live URLs are not required — approved is sufficient
    // For standard campaigns, every approved sub needs live_urls submitted
    const allLiveUrlsSubmitted = noLiveUrlTimeline
        ? approvedSubs.length > 0
        : approvedSubs.length > 0 && approvedSubs.every(s => (s.live_urls?.length ?? 0) >= (s.expected_count ?? 1));

    const fmt = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const addDays = (iso: string, days: number) => {
        const d = new Date(iso);
        d.setDate(d.getDate() + days);
        return d;
    };

    const hasApprovedSubs = Boolean(latestApprovedAt);
    const autoApproveDate = endDate ? addDays(endDate, autoApproveDays) : null;
    // Backend arms countdown when live URLs are ready (not only at approved_at).
    const markCompleteDeadlineAt = (workspace.participant as { mark_complete_deadline_at?: string } | undefined)
        ?.mark_complete_deadline_at;
    const autoCompleteDate = markCompleteDeadlineAt
        ? new Date(markCompleteDeadlineAt)
        : latestApprovedAt && allLiveUrlsSubmitted
        ? addDays(latestApprovedAt, markCompleteDays)
        : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Auto-approve benchmark — only when no approved subs yet */}
            {!hasApprovedSubs && autoApproveDate && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: BLUE_BG,
                        border: `1px solid ${BLUE_BORDER}`,
                    }}>
                    <AutorenewIcon sx={{ fontSize: 18, color: BLUE, mt: '1px', flexShrink: 0 }} />
                    <Box>
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: BLUE, mb: 0.25 }}>
                            Auto-approval on {fmt(autoApproveDate)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                            If you haven't reviewed and approved the creator's submission by{' '}
                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {autoApproveDays} day{autoApproveDays === 1 ? '' : 's'} after the campaign end date
                            </Box>
                            , the submission will be automatically approved.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Awaiting live URL — approved but live URLs not yet submitted */}
            {hasApprovedSubs && !allLiveUrlsSubmitted && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: AMBER_BG,
                        border: `1px solid ${AMBER_BORDER}`,
                    }}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: AMBER, mt: '1px', flexShrink: 0 }} />
                    <Box>
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: AMBER, mb: 0.25 }}>
                            Awaiting live post link
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                            The submission has been approved but the creator hasn't submitted their live social post
                            link yet. Send them a message in the discussion to remind them.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Auto-complete benchmark — only when live URLs are all in */}
            {hasApprovedSubs && allLiveUrlsSubmitted && autoCompleteDate && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: GREEN_BG,
                        border: `1px solid ${GREEN_BORDER}`,
                    }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: GREEN, mt: '1px', flexShrink: 0 }} />
                    <Box>
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: GREEN, mb: 0.25 }}>
                            Auto-complete on {fmt(autoCompleteDate)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                            If you don't mark this campaign as complete within{' '}
                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {markCompleteDays} day{markCompleteDays === 1 ? '' : 's'} after live post links are in
                            </Box>
                            , payment will be automatically released to the creator.
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
